import { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Collapse from '@mui/material/Collapse';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useUpdateAvailable } from '../lib/useUpdateAvailable';
import type { ChangelogEntry } from '../data/changelog';

/**
 * Dynamisches Banner ganz oben in der App: erscheint, sobald ein neueres Frontend
 * deployt wurde. Fordert zum Neuladen auf (kein Auto-Reload). Persistent.
 *
 * „Was ist neu": Bei erkanntem Update lädt das Banner /changelog.json (Stand der
 * NEUEN Version vom Server — das laufende Bundle kennt diesen changelog noch nicht)
 * und zeigt die Änderungen seit der laufenden Version (__APP_VERSION__), gruppiert
 * nach Features / Verbesserungen / Fixes — analog zum Council-Update-Banner.
 */
export function UpdateBanner() {
  const updateAvailable = useUpdateAvailable();
  const [open, setOpen] = useState(false);
  const [entries, setEntries] = useState<ChangelogEntry[]>([]);

  useEffect(() => {
    if (!updateAvailable) return;
    let active = true;
    (async () => {
      try {
        const res = await fetch(`/changelog.json?t=${Date.now()}`, { cache: 'no-store' });
        if (!res.ok) return;
        const all = (await res.json()) as ChangelogEntry[];
        // Einträge oberhalb der laufenden Version = das, was das Update bringt.
        const idx = all.findIndex((e) => e.version === __APP_VERSION__);
        const fresh = idx <= 0 ? all : all.slice(0, idx);
        if (active) setEntries(fresh);
      } catch {
        /* changelog.json fehlt / offline → Panel bleibt aus */
      }
    })();
    return () => { active = false; };
  }, [updateAvailable]);

  // Änderungen aller neuen Versionen in drei Gruppen zusammenfassen (Council-Stil).
  const groups = useMemo(() => {
    const g = { features: [] as string[], improvements: [] as string[], fixes: [] as string[] };
    for (const e of entries) {
      // Defensiv: Einträge müssen nicht alle drei Rubriken tragen (reine
      // Verbesserungs-/Fix-Releases) — fehlendes Array sonst -> Spread „not iterable".
      g.features.push(...(e.features ?? []));
      g.improvements.push(...(e.improvements ?? []));
      g.fixes.push(...(e.fixes ?? []));
    }
    return g;
  }, [entries]);

  if (!updateAvailable) return null;
  const total = groups.features.length + groups.improvements.length + groups.fixes.length;
  const hasNews = total > 0;
  const newVersion = entries.length > 0 ? entries[0]!.version : null;

  return (
    <Box
      role="alert"
      sx={{
        position: 'sticky', top: 0, zIndex: 2100,
        // Feste, theme-unabhängige Farben — muss in JEDEM Theme lesbar sein.
        bgcolor: '#ffb300', color: '#1a1a1a',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, px: 2, py: 0.75, flexWrap: 'wrap' }}>
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1a1a1a' }}>
          🔄 Eine neue Version{newVersion ? ` (v${newVersion})` : ''} ist verfügbar. Bitte lade die Seite neu, um auf dem aktuellen Stand zu arbeiten.
        </Typography>

        {hasNews && (
          <Button
            size="small"
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
            sx={{
              flexShrink: 0, fontWeight: 700, minWidth: 0,
              color: '#1a1a1a !important',
              backgroundColor: 'rgba(26,26,26,0.10) !important',
              backgroundImage: 'none !important',
              border: '1px solid rgba(26,26,26,0.4)',
              '&:hover': { backgroundColor: 'rgba(26,26,26,0.18) !important' },
            }}
          >
            Was ist neu {open ? '▴' : '▾'}
          </Button>
        )}

        <Button
          size="small"
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={() => window.location.reload()}
          sx={{
            flexShrink: 0, fontWeight: 700,
            color: '#ffffff !important',
            backgroundColor: '#1a1a1a !important',
            backgroundImage: 'none !important',
            border: '1px solid rgba(255,255,255,0.6)',
            '& .MuiButton-startIcon': { color: '#ffffff' },
            '&:hover': { backgroundColor: '#000000 !important' },
          }}
        >
          Jetzt neu laden
        </Button>
      </Box>

      {hasNews && (
        <Collapse in={open}>
          <Box
            sx={{
              bgcolor: '#fffdf5', color: '#1a1a1a',
              borderTop: '1px solid rgba(26,26,26,0.2)',
              px: 3, py: 1.5, maxHeight: 320, overflowY: 'auto',
            }}
          >
            <ChangeGroup emoji="✨" title="Features" items={groups.features} />
            <ChangeGroup emoji="🔧" title="Verbesserungen" items={groups.improvements} />
            <ChangeGroup emoji="🐛" title="Fixes" items={groups.fixes} />
          </Box>
        </Collapse>
      )}
    </Box>
  );
}

function ChangeGroup({ emoji, title, items }: { emoji: string; title: string; items: string[] }) {
  if (items.length === 0) return null;
  return (
    <Box sx={{ mb: 1.25 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#1a1a1a' }}>
        {emoji} {title} ({items.length})
      </Typography>
      <Box component="ul" sx={{ m: '2px 0 0', pl: 3 }}>
        {items.map((line, i) => (
          <Typography key={i} component="li" variant="body2" sx={{ color: '#1a1a1a', lineHeight: 1.45 }}>
            {line}
          </Typography>
        ))}
      </Box>
    </Box>
  );
}
